<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('vip.title') }}</span>
          <div class="header-actions-wrapper">
            <div class="header-actions">
              <el-dropdown @command="handleExportCommand" trigger="click">
                <el-button type="success">
                  <el-icon><Download /></el-icon>
                  {{ $t('common.export') }}
                  <el-icon class="el-icon--right"><ArrowDown /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="xlsx">
                      <el-icon><Document /></el-icon>
                      {{ $t('common.exportExcel') }}
                    </el-dropdown-item>
                    <el-dropdown-item command="csv">
                      <el-icon><Tickets /></el-icon>
                      {{ $t('common.exportCSV') }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              <el-button type="primary" @click="goToAdd">
                <el-icon><Plus /></el-icon>
                {{ $t('vip.newPackage') }}
              </el-button>
            </div>
            <el-button-group class="view-toggle">
              <el-button 
                :type="viewMode === 'table' ? 'primary' : ''" 
                @click="viewMode = 'table'"
              >
                <el-icon><List /></el-icon>
              </el-button>
              <el-button 
                :type="viewMode === 'card' ? 'primary' : ''" 
                @click="viewMode = 'card'"
              >
                <el-icon><Grid /></el-icon>
              </el-button>
            </el-button-group>
          </div>
        </div>
      </template>

      <!-- Filter Bar -->
      <div class="filter-bar">
        <el-radio-group v-model="activeFilter" size="default" @change="fetchPackages">
          <el-radio-button :value="false">📋 {{ $t('vip.allPackages') }}</el-radio-button>
          <el-radio-button :value="true">{{ $t('vip.activeOnly') }}</el-radio-button>
          <el-radio-button :value="'inactive'"> {{ $t('vip.inactiveOnly') }}</el-radio-button>
        </el-radio-group>
      </div>

      <!-- Table View -->
      <el-table 
        v-if="viewMode === 'table'"
        :data="vipStore.vipPackages" 
        v-loading="vipStore.loading" 
        stripe
        style="width: 100%"
        @row-click="handleRowClick"
      >
        <el-table-column type="index" width="50" />
        <el-table-column prop="name" :label="$t('vip.name')" min-width="150" show-overflow-tooltip />
        <el-table-column prop="packageType" :label="$t('vip.packageType')" width="120" align="center">
          <template #default="{ row }">
            <el-tag type="primary" size="small" v-if="row.packageTypeName">
              {{ row.packageTypeName }}
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="price" :label="$t('vip.price')" width="120" align="left">
          <template #default="{ row }">
            <span class="amount-text">¥{{ row.price?.toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="courseHours" :label="$t('vip.courseHours')" width="120" align="center">
          <template #default="{ row }">
            <el-tag type="info" size="small">{{ row.courseHours }}h</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="validityDays" :label="$t('vip.validityDays')" width="120" align="center">
          <template #default="{ row }">
            <el-tag type="success" size="small">{{ row.validityDays }}d</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="isGiftPackage" :label="$t('vip.isGiftPackage')" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.isGiftPackage ? 'warning' : 'info'" size="small">
              {{ row.isGiftPackage ? $t('common.yes') : $t('common.no') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="isActive" :label="$t('vip.status')" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.isActive ? 'success' : 'info'" size="small">
              {{ row.isActive ? $t('vip.active') : $t('vip.inactive') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" :label="$t('vip.description')" min-width="200" show-overflow-tooltip />
        <el-table-column :label="$t('vip.actions')" width="140" fixed="right" align="center">
          <template #default="{ row }">
            <el-tooltip :content="$t('common.edit')" placement="top">
              <el-button 
                type="primary" 
                size="small" 
                :icon="Edit"
                circle
                @click="handleEdit(row)"
              />
            </el-tooltip>
            <el-tooltip :content="row.isActive ? $t('vip.delete') : $t('vip.restore')" placement="top">
              <el-button 
                :type="row.isActive ? 'danger' : 'success'" 
                size="small" 
                :icon="row.isActive ? Delete : RefreshRight"
                circle
                @click="handleDeleteOrRestore(row)"
              />
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>

      <!-- Card View -->
      <div v-else class="card-grid">
        <el-card 
          v-for="pkg in vipStore.vipPackages" 
          :key="pkg.id" 
          class="vip-card"
          shadow="hover"
        >
          <div class="vip-card-header">
            <h3 class="vip-card-title">{{ pkg.name }}</h3>
            <div class="vip-card-tags">
              <el-tag :type="pkg.isActive ? 'success' : 'info'" size="small">
                {{ pkg.isActive ? $t('vip.active') : $t('vip.inactive') }}
              </el-tag>
              <el-tag v-if="pkg.isGiftPackage" type="warning" size="small">
                {{ $t('vip.gift') }}
              </el-tag>
            </div>
          </div>
          
          <div class="vip-card-body">
            <div class="vip-card-item" v-if="pkg.packageTypeName">
              <el-icon><Collection /></el-icon>
              <span class="vip-card-label">{{ $t('vip.packageType') }}:</span>
              <span class="vip-card-value">{{ pkg.packageTypeName }}</span>
            </div>
            <div class="vip-card-item">
              <el-icon><Money /></el-icon>
              <span class="vip-card-label">{{ $t('vip.price') }}:</span>
              <span class="vip-card-value amount">¥{{ pkg.price?.toFixed(2) }}</span>
            </div>
            <div class="vip-card-item">
              <el-icon><Clock /></el-icon>
              <span class="vip-card-label">{{ $t('vip.courseHours') }}:</span>
              <span class="vip-card-value">{{ pkg.courseHours }}h</span>
            </div>
            <div class="vip-card-item">
              <el-icon><Calendar /></el-icon>
              <span class="vip-card-label">{{ $t('vip.validityDays') }}:</span>
              <span class="vip-card-value">{{ pkg.validityDays }}d</span>
            </div>
            <div class="vip-card-item" v-if="pkg.description">
              <el-icon><Document /></el-icon>
              <span class="vip-card-label">{{ $t('vip.description') }}:</span>
              <p class="vip-card-description">{{ pkg.description }}</p>
            </div>
          </div>
          
          <div class="vip-card-footer">
            <el-button type="primary" size="small" @click="handleEdit(pkg)">
              <el-icon><Edit /></el-icon>
              {{ $t('common.edit') }}
            </el-button>
            <el-button 
              :type="pkg.isActive ? 'danger' : 'success'" 
              size="small" 
              @click="handleDeleteOrRestore(pkg)"
            >
              <el-icon><component :is="pkg.isActive ? Delete : RefreshRight" /></el-icon>
              {{ pkg.isActive ? $t('vip.delete') : $t('vip.restore') }}
            </el-button>
          </div>
        </el-card>
      </div>

      <el-empty v-if="!vipStore.vipPackages.length && !vipStore.loading" :description="$t('vip.noPackages')" />
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { List, Grid, Money, Clock, Calendar, Document, Edit, Download, ArrowDown, Tickets, Plus, Delete, RefreshRight } from '@element-plus/icons-vue'
import { useVipStore } from '@/stores/vip'
import { exportToExcel, exportToCSV } from '@/utils/export'

const router = useRouter()
const { t } = useI18n()
const vipStore = useVipStore()
const activeFilter = ref(true) // Default to active only
const viewMode = ref('table') // 'table' or 'card'

// Auto-switch to card view on mobile
const checkMobileView = () => {
  if (window.innerWidth < 768) {
    viewMode.value = 'card'
  } else {
    viewMode.value = 'table'
  }
}

onMounted(() => {
  checkMobileView()
  window.addEventListener('resize', checkMobileView)
  fetchPackages()
})

// Cleanup event listener
watch(() => viewMode.value, () => {
  // Optional: Save user preference to localStorage
  localStorage.setItem('vipViewMode', viewMode.value)
})

const fetchPackages = () => {
  const activeOnly = activeFilter.value === true
  const inactiveOnly = activeFilter.value === 'inactive'
  
  if (inactiveOnly) {
    // Fetch all and filter locally for inactive
    vipStore.fetchVipPackages(false).then(() => {
      vipStore.vipPackages = vipStore.vipPackages.filter(p => !p.isActive)
    })
  } else {
    vipStore.fetchVipPackages(activeOnly)
  }
}

const goToAdd = () => {
  router.push('/vip/add')
}

const handleEdit = (row) => {
  router.push(`/vip/edit/${row.id}`)
}

const handleDeleteOrRestore = async (row) => {
  const isDelete = row.isActive
  const confirmText = isDelete ? t('vip.confirmDelete') : t('vip.confirmRestore')
  const successMsg = isDelete ? t('vip.deleteSuccess') : t('vip.restoreSuccess')
  
  try {
    await ElMessageBox.confirm(confirmText, t('vip.confirmTitle'), {
      confirmButtonText: t('common.confirm'),
      cancelButtonText: t('common.cancel'),
      type: 'warning'
    })
    
    // Both delete and restore use the same toggle-active API
    const result = await vipStore.deletePackage(row.id)
    
    if (result.success) {
      ElMessage.success(result.message || successMsg)
      fetchPackages()
    } else {
      ElMessage.error(result.message || t('vip.toggleFailed'))
    }
  } catch (error) {
    if (error !== 'cancel') console.error(error)
  }
}

const handleExportCommand = (format) => {
  if (format === 'csv') {
    handleExport('csv')
  } else {
    handleExport('xlsx')
  }
}

const handleExport = (format = 'xlsx') => {
  try {
    const packages = vipStore.vipPackages
    
    if (packages.length === 0) {
      ElMessage.warning(t('common.noData'))
      return
    }
    
    // Transform data for export
    const exportData = packages.map(pkg => ({
      'ID': pkg.id,
      'Name': pkg.name || '',
      'Package Type': pkg.packageTypeName || '-',
      'Price': `¥${pkg.price?.toFixed(2)}`,
      'Course Hours': `${pkg.courseHours}h`,
      'Validity Days': `${pkg.validityDays}d`,
      'Is Gift Package': pkg.isGiftPackage ? t('common.yes') : t('common.no'),
      'Status': pkg.isActive ? t('vip.active') : t('vip.inactive'),
      'Description': pkg.description || ''
    }))
    
    // Export based on format
    if (format === 'csv') {
      exportToCSV(exportData, 'vip-packages')
    } else {
      exportToExcel(exportData, 'vip-packages', 'VIP Packages')
    }
  } catch (error) {
    console.error('Export failed:', error)
    ElMessage.error(error.message || t('common.exportFailed'))
  }
}

const handleRowClick = (row) => {
  // Optional: Click row to view details
  console.log('Row clicked:', row)
}
</script>

<style scoped>
.page-container {
  padding: 12px;
  background-color: var(--bg-color-page, #f5f7fa);
  transition: background-color 0.3s ease;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.card-header .header-actions-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.card-header .header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.card-header .view-toggle .el-button {
  padding: 8px 12px;
}

.card-header span {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  transition: color 0.3s ease;
}

.filter-bar {
  margin-bottom: 16px;
  padding: 0;
}

:deep(.el-radio-group) {
  display: flex;
  gap: 4px;
}

:deep(.el-radio-button__inner) {
  padding: 8px 16px;
  font-size: 13px;
}

.amount-text {
  font-weight: 600;
  color: var(--color-primary, var(--el-color-primary));
  font-size: 14px;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .card-header .header-actions-wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    width: 100%;
  }

  .card-header .header-actions {
    width: 100%;
    justify-content: center;
  }

  :deep(.el-radio-group) {
    width: 100%;
  }

  :deep(.el-radio-button) {
    flex: 1;
  }

  :deep(.el-radio-button__inner) {
    padding: 6px 8px;
    font-size: 12px;
  }
}

/* Enhanced table styling */
:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;
}

:deep(.el-table__header-wrapper) {
  background: var(--bg-color-base, #f5f7fa);
}

:deep(.el-table th) {
  font-weight: 600;
  color: var(--color-text-regular, #606266);
  padding: 10px 0;
  font-size: 13px;
}

:deep(.el-table__row) {
  transition: all 0.3s;
}

:deep(.el-table__row:hover) {
  background-color: var(--bg-color-light, #f5f7fa);
}

:deep(.el-table__row--striped) {
  td {
    background-color: var(--bg-color-base, #f5f7fa) !important;
    color: var(--color-text-primary, #303133) !important;
  }
  
  &:hover td {
    background-color: var(--bg-color-light, #f5f7fa) !important;
    color: var(--color-text-primary, #303133) !important;
  }
}

:deep(.el-table td) {
  padding: 10px 0;
  font-size: 13px;
  color: var(--color-text-primary, #303133);
}

/* Mobile-responsive table */
@media (max-width: 768px) {
  :deep(.el-table) {
    font-size: 12px;
  }
  
  :deep(.el-table .cell) {
    padding: 0 8px;
  }
  
  :deep(.el-table th) {
    padding: 8px 0;
    font-size: 12px;
  }
  
  :deep(.el-table td) {
    padding: 8px 0;
  }
  
  :deep(.el-button--small) {
    padding: 5px 8px;
    font-size: 12px;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .page-container {
    padding: 8px;
  }

  .card-header span {
    font-size: 15px;
  }

  .card-header .header-actions-wrapper {
    gap: 8px;
  }

  .card-header .header-actions {
    gap: 6px;
  }

  .card-header .header-actions .el-button {
    padding: 8px 12px;
    font-size: 13px;
  }

  .card-header .view-toggle .el-button {
    padding: 8px 10px;
  }

  .filter-bar {
    margin-bottom: 12px;
  }

  :deep(.el-radio-button__inner) {
    padding: 6px 8px;
    font-size: 11px;
  }
}
.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  padding: 12px 0;
}

.vip-card {
  transition: all 0.3s;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.vip-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  background: var(--bg-color-white, #fff);
}

.vip-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-color-lighter, #ebeef5);
  transition: border-color 0.3s ease;
}

.vip-card-tags {
  display: flex;
  gap: 6px;
  align-items: center;
}

.vip-card-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  margin: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.3s ease;
}

.vip-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.vip-card-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: var(--color-text-regular, #606266);
  font-size: 13px;
  transition: color 0.3s ease;
}

.vip-card-item .el-icon {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 14px;
}

.vip-card-label {
  font-weight: 500;
  white-space: nowrap;
}

.vip-card-value {
  flex: 1;
  color: var(--color-text-primary, #303133);
  word-break: break-word;
  transition: color 0.3s ease;
}

.vip-card-value.amount {
  font-weight: 600;
  color: var(--color-primary, var(--el-color-primary));
  font-size: 15px;
}

.vip-card-description {
  margin: 0;
  color: var(--color-text-secondary, #909399);
  font-size: 12px;
  line-height: 1.4;
  max-height: 48px;
  overflow-y: auto;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  transition: color 0.3s ease;
}

.vip-card-footer {
  display: flex;
  gap: 6px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--border-color-lighter, #ebeef5);
  transition: border-color 0.3s ease;
}

.vip-card-footer .el-button {
  flex: 1;
  padding: 8px 10px;
  font-size: 12px;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .page-container {
    padding: 8px;
  }
  
  .card-header span {
    font-size: 15px;
  }
  
  .filter-bar {
    padding: 10px;
  }
  
  .card-grid {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 10px 0;
  }
  
  .vip-card-title {
    font-size: 13px;
  }
  
  .vip-card-item {
    font-size: 12px;
    gap: 4px;
  }
  
  .vip-card-item .el-icon {
    font-size: 13px;
  }
  
  .vip-card-value.amount {
    font-size: 14px;
  }
  
  .vip-card-footer {
    flex-direction: row;
    gap: 6px;
  }
  
  .vip-card-footer .el-button {
    flex: 1;
    min-width: 0;
    padding: 7px 8px;
    font-size: 11px;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .vip-card-header {
    margin-bottom: 10px;
    padding-bottom: 8px;
  }
  
  .vip-card-title {
    font-size: 12px;
  }
  
  .vip-card-body {
    gap: 6px;
  }
  
  .vip-card-item {
    font-size: 11px;
  }
  
  .vip-card-footer .el-button {
    padding: 6px 7px;
    font-size: 10px;
  }
}
</style>
